import { isOverlayBagKey } from "src/overlayAttributes/personOverlayAttributeKeys";
import { overlayAttributeKey } from "./attributeDefaultsTypes";
import type { AttributeCatalogEntry } from "./attributeDefaultsTypes";

export type AttributeLabelsByKey = Partial<Record<string, string>>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const isCustomOverlayAttribute = (
  entry?: Pick<AttributeCatalogEntry, "kind" | "internal">,
  key?: string
): boolean => {
  if (entry?.internal) {
    return false;
  }
  return Boolean(key && isOverlayBagKey(key));
};

export const getImportedAttributeLabel = (
  entry: Pick<AttributeCatalogEntry, "key" | "label">
): string => entry.label?.trim() || entry.key;

export const resolveOverlayDisplayLabel = ({
  key,
  catalogLabel,
  labelsByKey = {},
}: {
  key: string;
  catalogLabel?: string | null | undefined;
  labelsByKey?: AttributeLabelsByKey;
}): string => {
  const override = labelsByKey[key]?.trim();
  if (override) {
    return override;
  }
  const imported = catalogLabel?.trim();
  if (imported) {
    return imported;
  }
  return key;
};

export const setAttributeDisplayLabel = (
  labels: AttributeLabelsByKey,
  key: string,
  nextValue: string,
  entry?: Pick<AttributeCatalogEntry, "kind" | "internal">
): AttributeLabelsByKey => {
  if (!isCustomOverlayAttribute(entry, key)) {
    return labels;
  }
  const branded = overlayAttributeKey(key);
  if (!branded) {
    return labels;
  }
  const next = { ...labels };
  if (nextValue === "") {
    delete next[branded];
    return next;
  }
  next[branded] = nextValue;
  return next;
};

export const labelsAreEqual = (
  left: AttributeLabelsByKey,
  right: AttributeLabelsByKey
): boolean => {
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
  for (const key of keys) {
    if ((left[key] ?? "") !== (right[key] ?? "")) {
      return false;
    }
  }
  return true;
};

export const pruneDefaultAttributeLabels = (
  labels: AttributeLabelsByKey,
  catalog: readonly AttributeCatalogEntry[]
): AttributeLabelsByKey => {
  const catalogByKey = new Map(catalog.map((entry) => [entry.key, entry]));
  const pruned: AttributeLabelsByKey = {};
  for (const [key, value] of Object.entries(labels)) {
    const trimmed = value?.trim();
    if (!trimmed) {
      continue;
    }
    const branded = overlayAttributeKey(key);
    if (!branded) {
      continue;
    }
    const entry = catalogByKey.get(key);
    if (!isCustomOverlayAttribute(entry, key)) {
      continue;
    }
    if (trimmed === getImportedAttributeLabel(entry ?? { key, label: key })) {
      continue;
    }
    pruned[branded] = trimmed;
  }
  return pruned;
};

export const toPersistedAttributeLabels = (
  labels: AttributeLabelsByKey
): { byKey: Record<string, string> } => {
  const byKey: Record<string, string> = {};
  for (const [key, value] of Object.entries(labels)) {
    const trimmed = value?.trim();
    if (trimmed) {
      byKey[key] = trimmed;
    }
  }
  return { byKey };
};

export const parsePersistedAttributeLabels = (
  raw: unknown
): AttributeLabelsByKey => {
  if (!isRecord(raw)) {
    return {};
  }
  const source = isRecord(raw["byKey"]) ? raw["byKey"] : raw;
  const labels: AttributeLabelsByKey = {};
  for (const [key, value] of Object.entries(source)) {
    const branded = overlayAttributeKey(key);
    if (!branded || !isOverlayBagKey(key) || typeof value !== "string") {
      continue;
    }
    const trimmed = value.trim();
    if (trimmed) {
      labels[branded] = trimmed;
    }
  }
  return labels;
};
